import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import ShipInfoListTable from "../components/ShipInfoListTable";
import { getErrorMessage, getPageDescription, getSuccessMessage, getUiInterface, getPageName } from "../../../locales/translationUtils";
import { useTranslation } from 'react-i18next';
import NavBar, { NavBarItem } from "../../../components/NavBar";
import { GridColDef, GridRenderCellParams, GridSortModel } from "@mui/x-data-grid";
import AlertOverlay from "../../../components/Alert";
import { useShipInfo } from "../../../apis/ShipInfo/ShipInfoProvider";
import { getShipInfo } from "../../../services/shipinfoService";
import { exportarParaExcel } from "../../../utils/exportarExcel";
import ExportExcelButton from "../../../components/ExportExcelButton";
import Paginacao from "../../../types/paginacao";
import { ShipInfo } from "../../../types/shipinfo";
import { FaPlane, FaInfoCircle, FaBan } from "react-icons/fa";
import ShippingInfoModal, { ShippingData } from '../components/ShippingInfoModal';

// A API so aplica o filtro de release quando inicio E fim estao presentes
// (ReleaseDAO.FindByFilter exige os dois, mesmo padrao do filtro de PU em CargoDAO).
// Para expressar "release >= 01/01/2021" sem limite superior, o fim usa uma
// data bem no futuro - nunca alcancada na pratica.
const RELEASED_FILTRO_DATA_MINIMA = '2021-01-01';
const RELEASED_FILTRO_DATA_MAXIMA_SEM_LIMITE = '2099-12-31';

const ListView: React.FC = () => {
  const { t } = useTranslation();
  const { shipInfos, apiResponse, fetchShipInfos, markShipInfoAsShipped } = useShipInfo();
  const navigate = useNavigate();
  const [alert, setAlert] = useState<{ message: string; variant: "success" | "danger" | "warning" | "info" } | null>(null);
  const [showModal, setShowModal] = useState(false);
  const handleCloseModal = () => setShowModal(false);

  const [selectedId, setSelectedId] = useState<number | null>(null);

    const handleAirIconClick = (id: string) => {
        setSelectedId(Number(id));
        setShowModal(true);
    };

    const handleSubmit = async (data: ShippingData) => {
        if (selectedId !== null) {
            try {
                await markShipInfoAsShipped(selectedId, data);
                setAlert({ message: getSuccessMessage("editShipInfo", { id: selectedId }), variant: "success" });
                fetchShipInfos(paginacao, searchParams);
            } catch (error) {
                setAlert({ message: getErrorMessage("editShipInfoError"), variant: "danger" });
            }
        } else {
            setAlert({ message: getErrorMessage("editNoId"), variant: "warning" });
        }

        setShowModal(false); 
    };

    
    // Ligado por padrao: a tela sempre abre mostrando so processos recentes.
    const [filtrarReleasedApartir2021, setFiltrarReleasedApartir2021] = useState(true);

    const [searchParams, setSearchParams] = useState({
        campo: "dawb",
        valor: "",
        shippedBy: "",
        destination: "",
        freightType: "",
        releasedBy: "",
        consignee: "",
        to: "",
        releaseDate: "",
        releasedInicio: RELEASED_FILTRO_DATA_MINIMA,
        releasedFim: RELEASED_FILTRO_DATA_MAXIMA_SEM_LIMITE,
    });

    const [paginationModel, setPaginationModel] = React.useState({
        pageSize: 40,
        page: 0,
    });

    const [paginacao, setPaginacao] = React.useState({
        pageNum: 0,
        quantityPerPage: 40
    } as Paginacao);

    const [exportando, setExportando] = useState(false);

    const [sortModel, setSortModel] = React.useState<GridSortModel>([]);

   const filterOptions = [
        { value: "dawb", label: "DAWB" },
        { value: "description", label: "Description" },
        { value: "ponumber", label: "PO Number" },
        { value: "wrnumber", label: "WR Number" },
        { value: "supplier", label: "Supplier" },
        { value: "invoicenumber", label: "Invoice Number" }
    ];

   useEffect(() => {
        const newPaginacao: Paginacao = {
            ...paginacao,
            pageNum: paginationModel.page,
            quantityPerPage: paginationModel.pageSize,
            sort: sortModel[0]?.field,
            order: sortModel[0]?.sort ?? undefined,
        };
        setPaginacao(newPaginacao);
        fetchShipInfos(newPaginacao, searchParams);
    }, [paginationModel, sortModel]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();

        const valor = (e.currentTarget as any)[1].value;
        const updatedSearchParams = { ...searchParams, valor };

        const filters = Object.fromEntries(
            Object.entries(updatedSearchParams)
            .filter(([_, value]) => value !== "")
        );

        fetchShipInfos(paginacao, filters);
    };

    /**
     * Liga/desliga o filtro de release sem exigir clique em "Search" - e um
     * checkbox, a resposta precisa ser imediata.
     */
    const handleToggleFiltrarReleased = (checked: boolean) => {
        setFiltrarReleasedApartir2021(checked);

        const updatedSearchParams = {
            ...searchParams,
            releasedInicio: checked ? RELEASED_FILTRO_DATA_MINIMA : '',
            releasedFim: checked ? RELEASED_FILTRO_DATA_MAXIMA_SEM_LIMITE : '',
        };
        setSearchParams(updatedSearchParams);

        const filters = Object.fromEntries(
            Object.entries(updatedSearchParams).filter(([, value]) => value !== "")
        );
        fetchShipInfos(paginacao, filters);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setSearchParams(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const navItems: NavBarItem[] = [
        { label: t('shipInfo.labels.home'), route: "/", iconName: "house-door" },
        { label: t('shipInfo.labels.shipped'), route: "/Shipinfo/Shipped", iconName: "list" }
    ];


const columns: GridColDef<ShipInfo>[] = [
  {
    field: "actions",
    headerName: "",
    width: 125,
    sortable: false,
    filterable: false,
    renderCell: (params: GridRenderCellParams<ShipInfo>) => (
      <div className="d-flex justify-content-center me-2">
        <Button
          variant="link"
          size="lg"
          onClick={() => handleAirIconClick(params.row.id)}
        >
          <FaPlane className="text-info" />
        </Button>
        <Button
          variant="link"
          size="lg"
          onClick={() => handleNavigation(`/shipinfo/${params.row.id}`)}
        >
          <FaInfoCircle className="text-primary" />
        </Button>
      </div>
    ),
  },
  {
    field: "shippedBy",
    headerName: t('shipInfo.fields.carrier'),
    width: 180,
    valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
      params?.row?.shippedBy || "-",
    renderCell: (params: GridRenderCellParams<ShipInfo>) => (
      <span style={{ color: "black" }}>{params?.row?.shippedBy || "-"}</span>
    ),
  },
  {
    field: "destination",
    headerName: t('shipInfo.fields.destination'),
    width: 180,
    valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
      params?.row?.destination?.description || "-",
    renderCell: (params: GridRenderCellParams<ShipInfo>) => (
      <span style={{ color: "black" }}>{params?.row?.destination?.description || "-"}</span>
    ),
  },
  {
    field: "freightType",
    headerName: t('shipInfo.fields.status'),
    width: 180,
    valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
      params?.row?.freightType?.description || "-",
    renderCell: (params: GridRenderCellParams<ShipInfo>) => (
      <span style={{ color: "black" }}>{params?.row?.freightType?.description || "-"}</span>
    ),
  },
  {
    field: "releasedBy",
    headerName: t('shipInfo.fields.carrier'),
    width: 180,
    valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
      params?.row?.releasedBy?.fullName || "-",
    renderCell: (params: GridRenderCellParams<ShipInfo>) => (
      <span style={{ color: "black" }}>{params?.row?.releasedBy?.fullName || "-"}</span>
    ),
  },
  {
    field: "consignee",
    headerName: t('shipInfo.fields.carrier'),
    width: 300,
    valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
      params?.row?.consignee?.name || "-",
    renderCell: (params: GridRenderCellParams<ShipInfo>) => (
      <span style={{ color: "black" }}>{params?.row?.consignee?.name || "-"}</span>
    ),
  },
  {
    field: "to",
    headerName: t('shipInfo.fields.destination'),
    width: 180,
    valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
      params?.row?.to?.name || "-",
    renderCell: (params) => {
      if (!params?.row?.to?.name) return <span>-</span>;
      return <span style={{ color: "black" }}>{params.row.to.name}</span>;
    },
  },
  {
    field: "released",
    headerName: t('shipInfo.fields.shipmentDate'),
    width: 180,
    valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
      params?.row?.released ?? "-",
    valueFormatter: (params: any) => {
      const dateStr = params?.value;
      if (!dateStr) return "-";
      try {
        const dateValue = new Date(dateStr);
        return dateValue.toLocaleDateString();
      } catch {
        return "-";
      }
    },
    renderCell: (params: GridRenderCellParams<ShipInfo>) => {
      if (!params?.row?.released) return <span>-</span>;
      try {
        const dateValue = new Date(params.row.released);
        return <span style={{ color: "black" }}>{dateValue.toLocaleDateString()}</span>;
      } catch {
        return <span>-</span>;
      }
    },
  }
];


    const handleNavigation = (route: string) => {
        navigate(route);
    };

    /**
     * Exporta para Excel todos os itens que batem com o filtro atual - nao so a
     * pagina visivel. Busca direto do servico, sem tocar no estado do ShipInfoProvider
     * que alimenta a grid.
     */
    const handleExportarExcel = async () => {
        setExportando(true);

        try {
            const filtros = Object.fromEntries(
                Object.entries(searchParams).filter(([, value]) => value !== "")
            );
            const resposta = await getShipInfo(
                { pageNum: 0, quantityPerPage: 100000 } as Paginacao,
                filtros
            );

            const linhas = (resposta?.data?.values ?? []).map((row: any) => ({
                [t('shipInfo.fields.carrier')]: row?.shippedBy || '-',
                [t('shipInfo.fields.destination')]: row?.destination?.description || '-',
                [t('shipInfo.fields.status')]: row?.freightType?.description || '-',
                [t('shipInfo.fields.releasedBy') || 'Released by']: row?.releasedBy?.fullName || '-',
                [t('shipInfo.fields.consignee') || 'Consignee']: row?.consignee?.name || '-',
                [t('shipInfo.fields.to') || 'To']: row?.to?.name || '-',
                [t('shipInfo.fields.shipmentDate')]: row?.released ? new Date(row.released).toLocaleDateString() : '-',
            }));

            if (!(await exportarParaExcel(linhas, 'ship_info'))) {
                setAlert({ message: getUiInterface('exportEmptyResult'), variant: 'warning' });
            }
        } catch (error) {
            setAlert({ message: getUiInterface('exportError'), variant: 'danger' });
        } finally {
            setExportando(false);
        }
    };

    return (
        <Container fluid style={{ height: 'calc(100vh - 150px)', display: 'flex', flexDirection: 'column' }}>
            <ShippingInfoModal
                show={showModal}
                onClose={handleCloseModal}
                onSubmit={handleSubmit}
            />
            <NavBar items={navItems} />
            <div className="mb-2"></div>
            <h2>{getUiInterface("search")}</h2>
            <div className="mb-2"></div>
            
            {alert && (
                <AlertOverlay
                    message={alert.message}
                    variant={alert.variant}
                    onClose={() => setAlert(null)}
                />
            )}

            <div className="mb-1 p-3 bg-light rounded">
                <h5 className="mb-2">{getUiInterface("searchFor")}</h5>
                
                <Form onSubmit={handleSearch}>
                <Row className="mb-2">
                    <Col md={3}>
                        <Form.Group controlId="campo">
                            <Form.Select
                                name="campo"
                                value={searchParams.campo}
                                onChange={handleInputChange}
                            >
                                {filterOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group controlId="valor">
                            <Form.Control
                                type="text"
                                name="valor"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={2} className="d-flex align-items-end">
                        <div className="d-flex align-items-center" style={{ gap: '12px' }}>
                            <Button variant="primary" type="submit">
                                Search
                            </Button>
                            <Form.Check
                                type="checkbox"
                                id="filtro-released-apartir-2021"
                                label={getUiInterface('filterReleasedFrom2021')}
                                checked={filtrarReleasedApartir2021}
                                onChange={(e) => handleToggleFiltrarReleased(e.target.checked)}
                            />
                        </div>
                    </Col>
                </Row>
            </Form>
            </div>

            <h3 className="mb-2">{getPageDescription("releaseList")}</h3>
            <div className="d-flex justify-content-end mb-2">
                <ExportExcelButton onClick={handleExportarExcel} exportando={exportando} />
            </div>
            <div style={{ flex: 1, minHeight: 0 }}>
                <ShipInfoListTable
                    rows={shipInfos || []}
                    columns={columns}
                    getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row")}
                    apiResponse={apiResponse}
                    paginationModel={paginationModel}
                    setPaginationModel={setPaginationModel}
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                />
            </div>
        </Container>
    );
};

export default ListView;